import type { JSX } from 'react'

import { Button, Flex, Tooltip } from '@buffer-mono/popcorn'

import {
  SyncPopoverContent,
  SyncSection,
  SyncSectionText,
} from '~publish/components/SyncPopover'
import {
  CommentsSyncSectionIcon,
  LikesSyncSection,
  MentionsSyncSection,
} from './syncSections'

import styles from './SingleChannelSyncContent.module.css'

const RATE_LIMIT_EXPLANATION =
  'This channel can be synced every 1 hour. The option to sync will appear again once an hour has passed since your last sync.'

/**
 * Whether a manual sync is offered. `hidden` covers members without permission
 * to run one; `disabled` covers the hour-long rate limit between syncs.
 */
export type SyncNowState = 'hidden' | 'enabled' | 'disabled'

/**
 * Body of the single-channel sync popover. Presentational on purpose: the
 * backfill orchestration lives in the caller, so every state here can be
 * rendered on its own.
 */
export const SingleChannelSyncContent = ({
  isRealtimeSync,
  timeAgo,
  nextSyncTimeRange,
  supportsMentions,
  platform,
  syncNow,
  onSyncNow,
}: {
  isRealtimeSync: boolean
  /** Rendered in the title when the channel is polled, e.g. "5 minutes ago". */
  timeAgo: string
  nextSyncTimeRange: string
  supportsMentions: boolean
  platform: string
  syncNow: SyncNowState
  onSyncNow: () => void
}): JSX.Element => (
  <SyncPopoverContent
    title={
      isRealtimeSync
        ? 'This channel is updated automatically'
        : `Last synced ${timeAgo}`
    }
  >
    <SyncSection
      icon={<CommentsSyncSectionIcon size="small" />}
      label="Comments & replies"
    >
      {isRealtimeSync ? (
        <SyncSectionText>Updated every 2 min</SyncSectionText>
      ) : (
        <>
          <SyncSectionText>
            {nextSyncTimeRange === 'to run soon'
              ? 'Updated periodically. Next sync is scheduled to run soon.'
              : `Updated periodically. Next sync ${nextSyncTimeRange}`}
          </SyncSectionText>
          {/* The button is a row of its own: sitting inline, it trails the
              description text and lands mid-sentence whenever that text
              stops short of the full width. */}
          {syncNow !== 'hidden' && (
            <Flex className={styles.syncNowRow}>
              {syncNow === 'disabled' ? (
                <Tooltip content={RATE_LIMIT_EXPLANATION}>
                  <span className={styles.syncNowWrapper}>
                    <Button size="small" variant="primary" disabled>
                      Sync Now
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button size="small" variant="primary" onClick={onSyncNow}>
                  Sync Now
                </Button>
              )}
            </Flex>
          )}
        </>
      )}
    </SyncSection>
    {supportsMentions && <MentionsSyncSection />}
    <LikesSyncSection platform={platform} />
  </SyncPopoverContent>
)
